// Template Builder Drawer:
// Comprehensive template composer supporting all WhatChimp template types:
// General, Mixed buttons, Carousel (Media & Product), and Authentication (OTP).
// Integrates with MediaUploader, VariablesTable, AiRewriteModal, and CarouselCardsBuilder,
// with a live interactive phone preview side-by-side.

import { useEffect, useMemo, useState } from 'react';
import { useI18n } from '../../lib/i18n';
import Drawer from '../ui/Drawer';
import Button from '../ui/Button';
import TemplatePreview from './TemplatePreview';
import MediaUploader from './MediaUploader';
import VariablesTable from './VariablesTable';
import AiRewriteModal from './AiRewriteModal';
import CarouselCardsBuilder from './CarouselCardsBuilder';
import {
  extractVariables,
  groupButtonsForMeta,
  isValidTemplateName,
  validateMixedButtonsOrder,
} from '../../lib/templateText';
import { searchTemplateLocales } from '../../lib/templateLocales';
import { useCustomFields } from '../../lib/crm';
import {
  submitTemplate,
  type SubmitTemplateInput,
  type TemplateView,
} from '../../lib/whatsappApi';
import {
  COMMON_CUSTOM_FIELDS,
  type TplButton,
  type TplButtonType,
  type TplCategory,
  type TplHeaderType,
  type TplTemplateType,
  type TplCarouselCard,
  type WaTemplateHeaderMedia,
  type WaTemplateVariable,
  type TplComponent,
} from '../../lib/templateTypes';

export interface TemplateDrawerProps {
  open: boolean;
  onClose: () => void;
  onSubmitted: () => void;
  initialType?: TplTemplateType;
  templateToEdit?: TemplateView | null;
  integrationId?: string | null;
}

export default function TemplateDrawer({
  open,
  onClose,
  onSubmitted,
  initialType = 'general',
  templateToEdit = null,
  integrationId,
}: TemplateDrawerProps) {
  const { t } = useI18n();
  const { customFields: tenantCustomFields } = useCustomFields();

  const availableCustomFields = useMemo(() => {
    if (tenantCustomFields && tenantCustomFields.length > 0) {
      return tenantCustomFields.map((cf) => ({ key: cf.key, label: cf.label }));
    }
    return COMMON_CUSTOM_FIELDS;
  }, [tenantCustomFields]);

  // Mode & Meta
  const [templateType, setTemplateType] = useState<TplTemplateType>(initialType);
  const [name, setName] = useState('');
  const [category, setCategory] = useState<TplCategory>('MARKETING');
  const [language, setLanguage] = useState<string>('en');
  const [localeSearch, setLocaleSearch] = useState('');

  // Header State
  const [headerType, setHeaderType] = useState<TplHeaderType>('NONE');
  const [headerText, setHeaderText] = useState('');
  const [headerMedia, setHeaderMedia] = useState<WaTemplateHeaderMedia | null>(null);
  const [headerMediaPreviewUrl, setHeaderMediaPreviewUrl] = useState<string | null>(null);
  const [headerMediaName, setHeaderMediaName] = useState<string | null>(null);

  // Body & Footer State
  const [body, setBody] = useState('Hi {{1}}, thank you for choosing us!');
  const [footer, setFooter] = useState('');

  // Buttons State
  const [buttons, setButtons] = useState<TplButton[]>([]);

  // Carousel Cards State
  const [carouselCards, setCarouselCards] = useState<TplCarouselCard[]>([
    {
      id: 'card_1',
      mediaType: 'IMAGE',
      bodyText: 'Special offer card 1',
      buttons: [{ type: 'URL', text: 'View Offer', url: 'https://example.com/1' }],
    },
    {
      id: 'card_2',
      mediaType: 'IMAGE',
      bodyText: 'Special offer card 2',
      buttons: [{ type: 'URL', text: 'View Offer', url: 'https://example.com/2' }],
    },
  ]);

  // Variables & UI Modals
  const [variables, setVariables] = useState<WaTemplateVariable[]>([
    { index: 1, source: 'name', example: 'Sarah' },
  ]);
  const [aiModalOpen, setAiModalOpen] = useState(false);
  const [customFieldDropdownOpen, setCustomFieldDropdownOpen] = useState(false);

  // Submission & Validation
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);

  // Sync initial type or template to edit when drawer opens
  useEffect(() => {
    if (open) {
      if (templateToEdit) {
        setName(`${templateToEdit.name}_copy`);
        setCategory(templateToEdit.category);
        setLanguage(templateToEdit.language || 'en');
        setBody(templateToEdit.body || '');
        if (templateToEdit.headerMedia) {
          setHeaderType('IMAGE');
          setHeaderMedia(templateToEdit.headerMedia);
        }
        if (templateToEdit.variables && templateToEdit.variables.length > 0) {
          setVariables(templateToEdit.variables);
        }
      } else {
        setTemplateType(initialType);
        if (initialType === 'otp') {
          setCategory('AUTHENTICATION');
          setBody('Your verification code is {{1}}. This code expires in 10 minutes.');
          setButtons([{ type: 'OTP', text: 'Copy Code' }]);
          setVariables([{ index: 1, source: 'static', example: '123456' }]);
        } else if (initialType === 'carousel_media' || initialType === 'carousel_product') {
          setCategory('MARKETING');
          setBody('Browse our top recommendations below:');
        }
      }
    }
  }, [open, initialType, templateToEdit]);

  // Automatically sync variables when body or header text changes
  useEffect(() => {
    const combinedText = `${headerText} ${body}`;
    const detectedIndices = extractVariables(combinedText);

    setVariables((prev) => {
      const prevMap = new Map(prev.map((v) => [v.index, v]));
      return detectedIndices.map((idx) => {
        if (prevMap.has(idx)) {
          return prevMap.get(idx)!;
        }
        return {
          index: idx,
          source: idx === 1 ? 'name' : 'static',
          example: idx === 1 ? 'Customer' : '',
        };
      });
    });
  }, [headerText, body]);

  // Filtered Locales
  const filteredLocales = useMemo(
    () => searchTemplateLocales(localeSearch),
    [localeSearch],
  );

  // Quick Insertion Helpers
  function insertAtCursor(source: WaTemplateVariable['source'] = 'static', example = '') {
    const nextIdx = extractVariables(body).length + 1;
    const placeholder = `{{${nextIdx}}}`;
    setBody((b) => `${b} ${placeholder}`);
    setVariables((prev) => [
      ...prev.filter((v) => v.index !== nextIdx),
      { index: nextIdx, source, example },
    ]);
  }

  // Button Management
  function addButton(type: TplButtonType) {
    if (buttons.length >= 10) return;
    const newBtn: TplButton = {
      type,
      text:
        type === 'QUICK_REPLY'
          ? 'Quick Reply'
          : type === 'URL'
          ? 'Visit Website'
          : type === 'PHONE_NUMBER'
          ? 'Call Us'
          : type === 'COPY_CODE'
          ? '20OFF'
          : 'Copy Code',
      url: type === 'URL' ? 'https://example.com' : undefined,
      phone_number: type === 'PHONE_NUMBER' ? '+15551234567' : undefined,
    };
    setButtons((bs) => [...bs, newBtn]);
  }

  function updateButton(idx: number, patch: Partial<TplButton>) {
    setButtons((bs) => bs.map((b, i) => (i === idx ? { ...b, ...patch } : b)));
  }

  function removeButton(idx: number) {
    setButtons((bs) => bs.filter((_, i) => i !== idx));
  }

  // Mixed buttons validation
  const mixedValidation = useMemo(
    () => validateMixedButtonsOrder(buttons),
    [buttons],
  );

  // Form Validation
  const nameValid = isValidTemplateName(name);
  const bodyValid = body.trim().length > 0 && body.length <= 1024;
  const headerValid = headerType === 'NONE' || (headerType === 'TEXT' ? headerText.length <= 60 : !!headerMedia);
  const footerValid = !footer || footer.length <= 60;
  const variablesValid = variables.every((v) => v.example && v.example.trim().length > 0);
  const carouselValid =
    templateType !== 'carousel_media' && templateType !== 'carousel_product'
      ? true
      : carouselCards.length >= 2 &&
        carouselCards.length <= 10 &&
        carouselCards.every((c) => c.bodyText.length <= 160);

  const canSubmit =
    nameValid &&
    bodyValid &&
    headerValid &&
    footerValid &&
    variablesValid &&
    mixedValidation.valid &&
    carouselValid &&
    !submitting;

  // Submit Handler
  async function handleSubmit() {
    if (!canSubmit) return;
    setSubmitting(true);
    setSubmitError(null);

    try {
      const components: TplComponent[] = [];

      // 1. Header Component
      if (headerType === 'TEXT' && headerText.trim()) {
        const headerVars = extractVariables(headerText);
        const headerExamples = headerVars
          .map((idx) => variables.find((v) => v.index === idx)?.example)
          .filter(Boolean) as string[];

        components.push({
          type: 'HEADER',
          format: 'TEXT',
          text: headerText.trim(),
          example: headerExamples.length > 0 ? { header_text: headerExamples } : undefined,
        });
      } else if (headerType !== 'NONE' && headerMedia) {
        components.push({
          type: 'HEADER',
          format: headerType,
          example: {
            header_handle: [headerMedia.handle],
          },
        });
      }

      // 2. Body Component
      const bodyVars = extractVariables(body);
      const bodyExamples = bodyVars.map((idx) => {
        const found = variables.find((v) => v.index === idx);
        return found?.example || 'sample';
      });

      components.push({
        type: 'BODY',
        text: body.trim(),
        example: bodyExamples.length > 0 ? { body_text: [bodyExamples] } : undefined,
      });

      // 3. Footer Component
      if (footer.trim()) {
        components.push({
          type: 'FOOTER',
          text: footer.trim(),
        });
      }

      // 4. Buttons Component
      if (
        templateType !== 'carousel_media' &&
        templateType !== 'carousel_product' &&
        buttons.length > 0
      ) {
        // Enforce grouped quick reply order
        const orderedButtons = groupButtonsForMeta(buttons);
        components.push({
          type: 'BUTTONS',
          buttons: orderedButtons.map((b) => ({
            type: b.type,
            text: b.text.trim(),
            url: b.url?.trim(),
            phone_number: b.phone_number?.trim(),
          })),
        });
      }

      // 5. Carousel Component
      if (templateType === 'carousel_media' || templateType === 'carousel_product') {
        components.push({
          type: 'CAROUSEL',
          cards: carouselCards.map((c) => ({
            components: [
              {
                type: 'HEADER',
                format: c.mediaType,
                example: c.mediaHandle ? { header_handle: [c.mediaHandle] } : undefined,
              },
              {
                type: 'BODY',
                text: c.bodyText,
              },
              ...(c.buttons.length > 0
                ? [
                    {
                      type: 'BUTTONS',
                      buttons: c.buttons.map((b) => ({
                        type: b.type,
                        text: b.text,
                        url: b.url,
                      })),
                    },
                  ]
                : []),
            ],
          })),
        });
      }

      const payload: SubmitTemplateInput = {
        name: name.trim().toLowerCase(),
        language,
        category,
        body: body.trim(),
        variablesCount: variables.length,
        variables,
        components,
        headerMedia: headerMedia || undefined,
        integrationId: integrationId || undefined,
      };

      await submitTemplate(payload);
      onSubmitted();
      onClose();
    } catch (err) {
      setSubmitError((err as Error).message || 'Failed to submit template to Meta');
    } finally {
      setSubmitting(false);
    }
  }

  const isCarousel = templateType === 'carousel_media' || templateType === 'carousel_product';

  return (
    <Drawer
      open={open}
      onClose={onClose}
      title={
        <div className="flex items-center gap-2">
          <span>{templateToEdit ? t('tpl.editCopy') || 'Duplicate Template' : t('tpl.create') || 'Create WhatsApp Template'}</span>
          <span className="rounded-full bg-indigo-50 px-2.5 py-0.5 text-xs font-semibold uppercase text-indigo-700">
            {templateType.replace('_', ' ')}
          </span>
        </div>
      }
      subtitle={t('tpl.drawerSubtitle') || 'Configure template components, upload media samples, and submit for Meta approval.'}
      size="2xl"
      phonePreview={
        <TemplatePreview
          headerType={headerType}
          headerText={headerText}
          headerMediaUrl={headerMediaPreviewUrl}
          headerMediaName={headerMediaName}
          body={body}
          footer={footer}
          buttons={isCarousel ? [] : buttons}
          variables={variables}
          carouselCards={isCarousel ? carouselCards : []}
        />
      }
      footer={
        <div className="flex items-center justify-between gap-3">
          <div className="text-xs text-slate-500">
            {!variablesValid && (
              <span className="text-amber-600 font-semibold">
                ⚠️ Fill in all sample variable values before submitting
              </span>
            )}
            {!nameValid && name.length > 0 && (
              <span className="text-red-600 font-semibold">
                ⚠️ Template name must only use lowercase letters, numbers, and underscores
              </span>
            )}
            {!mixedValidation.valid && (
              <span className="text-red-600 font-semibold">
                ⚠️ {mixedValidation.error}
              </span>
            )}
          </div>

          <div className="flex items-center gap-2 shrink-0">
            <Button variant="ghost" onClick={onClose} disabled={submitting}>
              {t('tpl.cancel') || 'Cancel'}
            </Button>
            <Button
              variant="primary"
              onClick={handleSubmit}
              disabled={!canSubmit}
              loading={submitting}
            >
              {submitting
                ? t('tpl.submitting') || 'Submitting to Meta...'
                : t('tpl.submitForReview') || 'Submit to Meta for Review'}
            </Button>
          </div>
        </div>
      }
    >
      <div className="space-y-6 pb-6">
        {/* Template Type Selector Tabs */}
        <div className="border-b border-slate-200 pb-4">
          <label className="block text-xs font-bold uppercase tracking-wider text-slate-500 mb-2">
            {t('tpl.type.label') || 'Template Type'}
          </label>
          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-5 gap-1.5 rounded-xl bg-slate-100 p-1">
            {(
              [
                { id: 'general', label: 'General' },
                { id: 'mixed', label: 'Mixed Buttons' },
                { id: 'carousel_media', label: 'Carousel (Media)' },
                { id: 'carousel_product', label: 'Carousel (Product)' },
                { id: 'otp', label: 'Auth (OTP)' },
              ] as const
            ).map((tab) => (
              <button
                key={tab.id}
                type="button"
                onClick={() => {
                  setTemplateType(tab.id);
                  if (tab.id === 'otp') {
                    setCategory('AUTHENTICATION');
                  }
                }}
                className={`rounded-lg px-2.5 py-1.5 text-xs font-semibold transition-all ${
                  templateType === tab.id
                    ? 'bg-white text-indigo-700 shadow-sm'
                    : 'text-slate-600 hover:text-slate-900'
                }`}
              >
                {tab.label}
              </button>
            ))}
          </div>
        </div>

        {/* Section 1: Template Basic Info */}
        <div className="space-y-4 rounded-xl border border-slate-200 bg-white p-4 shadow-sm">
          <h3 className="text-xs font-bold uppercase tracking-wider text-slate-700">
            {t('tpl.section.basic') || '1. Basic Configuration'}
          </h3>

          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            {/* Template Name */}
            <div>
              <div className="flex justify-between items-center mb-1">
                <label className="text-xs font-semibold text-slate-700">
                  {t('tpl.form.name') || 'Template Name'}
                </label>
                <span className="text-[10px] text-slate-400 font-mono">
                  {name.length}/512
                </span>
              </div>
              <input
                type="text"
                value={name}
                onChange={(e) => setName(e.target.value.toLowerCase().replace(/\s+/g, '_'))}
                placeholder="summer_sale_promo"
                maxLength={512}
                className={`w-full rounded-lg border px-3 py-2 text-xs font-mono outline-none ${
                  name.length > 0 && !nameValid
                    ? 'border-red-500 focus:border-red-500'
                    : 'border-slate-200 focus:border-indigo-500'
                }`}
              />
              <p className="mt-1 text-[10px] text-slate-400">
                Lowercase letters, numbers, underscores only (e.g. order_update_v1)
              </p>
            </div>

            {/* Category */}
            <div>
              <label className="block text-xs font-semibold text-slate-700 mb-1">
                {t('tpl.form.category') || 'Category'}
              </label>
              <select
                value={category}
                disabled={templateType === 'otp'}
                onChange={(e) => setCategory(e.target.value as TplCategory)}
                className="w-full rounded-lg border border-slate-200 bg-white px-3 py-2 text-xs text-slate-800 outline-none focus:border-indigo-500"
              >
                <option value="MARKETING">Marketing (Promotions, updates, announcements)</option>
                <option value="UTILITY">Utility (Account alerts, order updates, billing)</option>
                <option value="AUTHENTICATION">Authentication (One-time passcodes, verification)</option>
              </select>
              <p className="mt-1 text-[10px] text-slate-400">
                Determines Meta template approval rules and messaging rates.
              </p>
            </div>

            {/* Language Selection with Search */}
            <div className="sm:col-span-2">
              <label className="block text-xs font-semibold text-slate-700 mb-1">
                {t('tpl.form.language') || 'Language / Locale'}
              </label>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                <input
                  type="text"
                  value={localeSearch}
                  onChange={(e) => setLocaleSearch(e.target.value)}
                  placeholder="Filter languages (e.g. Arabic, Spanish, en, ar)..."
                  className="rounded-lg border border-slate-200 px-3 py-1.5 text-xs outline-none focus:border-indigo-500"
                />
                <select
                  value={language}
                  onChange={(e) => setLanguage(e.target.value)}
                  className="rounded-lg border border-slate-200 bg-white px-3 py-1.5 text-xs text-slate-800 outline-none focus:border-indigo-500"
                >
                  {filteredLocales.map((loc) => (
                    <option key={loc.code} value={loc.code}>
                      {loc.name} ({loc.nativeName}) — [{loc.code}]
                    </option>
                  ))}
                </select>
              </div>
            </div>
          </div>
        </div>

        {/* Section 2: Header Builder (General & Mixed only) */}
        {!isCarousel && templateType !== 'otp' && (
          <div className="space-y-3 rounded-xl border border-slate-200 bg-white p-4 shadow-sm">
            <h3 className="text-xs font-bold uppercase tracking-wider text-slate-700">
              {t('tpl.section.header') || '2. Header (Optional)'}
            </h3>

            {/* Header Type Radio Group */}
            <div className="flex flex-wrap gap-2">
              {(
                [
                  { id: 'NONE', label: 'None' },
                  { id: 'TEXT', label: 'Text' },
                  { id: 'IMAGE', label: 'Image' },
                  { id: 'VIDEO', label: 'Video' },
                  { id: 'DOCUMENT', label: 'Document (PDF)' },
                ] as const
              ).map((ht) => (
                <button
                  key={ht.id}
                  type="button"
                  onClick={() => setHeaderType(ht.id)}
                  className={`rounded-lg px-3 py-1.5 text-xs font-semibold border transition-all ${
                    headerType === ht.id
                      ? 'border-indigo-600 bg-indigo-50/80 text-indigo-700'
                      : 'border-slate-200 text-slate-600 hover:border-slate-300'
                  }`}
                >
                  {ht.label}
                </button>
              ))}
            </div>

            {/* Text Header Input */}
            {headerType === 'TEXT' && (
              <div className="pt-2">
                <div className="flex justify-between items-center mb-1">
                  <label className="text-xs font-semibold text-slate-700">
                    Header Text
                  </label>
                  <span
                    className={`text-[10px] ${
                      headerText.length > 60 ? 'text-red-600 font-bold' : 'text-slate-400'
                    }`}
                  >
                    {headerText.length}/60
                  </span>
                </div>
                <input
                  type="text"
                  maxLength={60}
                  value={headerText}
                  onChange={(e) => setHeaderText(e.target.value)}
                  placeholder={t('tpl.subjectPlaceholder') || 'e.g. Exclusive Offer for You {{1}}'}
                  className="w-full rounded-lg border border-slate-200 px-3 py-2 text-xs outline-none focus:border-indigo-500"
                />
              </div>
            )}

            {/* Media Header Uploader */}
            {(headerType === 'IMAGE' || headerType === 'VIDEO' || headerType === 'DOCUMENT') && (
              <div className="pt-2">
                <MediaUploader
                  mediaType={headerType}
                  value={headerMedia}
                  onChange={(media, previewUrl, fileName) => {
                    setHeaderMedia(media);
                    setHeaderMediaPreviewUrl(previewUrl || null);
                    setHeaderMediaName(fileName || null);
                  }}
                  integrationId={integrationId || undefined}
                />
              </div>
            )}
          </div>
        )}

        {/* Section 3: Body Editor */}
        <div className="space-y-3 rounded-xl border border-slate-200 bg-white p-4 shadow-sm">
          <div className="flex items-center justify-between">
            <h3 className="text-xs font-bold uppercase tracking-wider text-slate-700">
              {t('tpl.section.body') || (isCarousel ? '2. Main Message Bubble' : '3. Message Body')}
            </h3>
            <span
              className={`text-[10px] ${
                body.length > 1024 ? 'text-red-600 font-bold' : 'text-slate-400'
              }`}
            >
              {body.length}/1024
            </span>
          </div>

          {/* Body Insert Menu Toolbar */}
          <div className="flex flex-wrap items-center gap-1.5 border-b border-slate-100 pb-2">
            <span className="text-[11px] font-semibold text-slate-500 me-1">Insert:</span>

            {/* Insert Name */}
            <button
              type="button"
              onClick={() => insertAtCursor('name', 'Customer')}
              className="rounded-lg border border-slate-200 bg-slate-50 px-2.5 py-1 text-xs font-semibold text-slate-700 hover:bg-slate-100"
            >
              + {t('tpl.insert.name') || 'Name'}
            </button>

            {/* Insert Generic Variable */}
            <button
              type="button"
              onClick={() => insertAtCursor('static', 'Value')}
              className="rounded-lg border border-slate-200 bg-slate-50 px-2.5 py-1 text-xs font-semibold text-slate-700 hover:bg-slate-100"
            >
              + {t('tpl.insert.variable') || 'Variable {{n}}'}
            </button>

            {/* Insert Custom Field Dropdown */}
            <div className="relative">
              <button
                type="button"
                onClick={() => setCustomFieldDropdownOpen(!customFieldDropdownOpen)}
                className="rounded-lg border border-slate-200 bg-slate-50 px-2.5 py-1 text-xs font-semibold text-slate-700 hover:bg-slate-100 flex items-center gap-1"
              >
                <span>+ {t('tpl.insert.customField') || 'Custom Field'}</span>
                <span>▾</span>
              </button>

              {customFieldDropdownOpen && (
                <div className="absolute start-0 top-full z-20 mt-1 w-56 rounded-xl border border-slate-200 bg-white p-1 shadow-lg divide-y divide-slate-100 max-h-60 overflow-y-auto">
                  <div className="py-1">
                    {availableCustomFields.map((cf) => (
                      <button
                        key={cf.key}
                        type="button"
                        onClick={() => {
                          insertAtCursor(cf.key as WaTemplateVariable['source'], 'Sample');
                          setCustomFieldDropdownOpen(false);
                        }}
                        className="block w-full text-start px-2.5 py-1.5 text-xs text-slate-700 hover:bg-indigo-50 hover:text-indigo-700 rounded-md"
                      >
                        {cf.label}
                      </button>
                    ))}
                  </div>
                </div>
              )}
            </div>

            {/* AI Rewrite Button */}
            <button
              type="button"
              onClick={() => setAiModalOpen(true)}
              className="ms-auto flex items-center gap-1 rounded-lg border border-indigo-200 bg-indigo-50 px-3 py-1 text-xs font-bold text-indigo-700 hover:bg-indigo-100 shadow-sm"
            >
              <span>✨</span>
              <span>{t('tpl.ai.rewrite') || 'Rewrite with AI'}</span>
            </button>
          </div>

          <textarea
            value={body}
            maxLength={1024}
            rows={4}
            dir={language === 'ar' ? 'rtl' : 'ltr'}
            onChange={(e) => setBody(e.target.value)}
            className="w-full rounded-lg border border-slate-200 p-3 text-xs leading-relaxed outline-none focus:border-indigo-500 font-sans"
            placeholder="Hi {{1}}, thank you for contacting us..."
          />

          {/* Variables Table */}
          <VariablesTable variables={variables} onChange={setVariables} />
        </div>

        {/* Section 4: Footer Builder (General & Mixed only) */}
        {!isCarousel && templateType !== 'otp' && (
          <div className="space-y-2 rounded-xl border border-slate-200 bg-white p-4 shadow-sm">
            <div className="flex items-center justify-between">
              <h3 className="text-xs font-bold uppercase tracking-wider text-slate-700">
                {t('tpl.section.footer') || '4. Footer (Optional)'}
              </h3>
              <span
                className={`text-[10px] ${
                  footer.length > 60 ? 'text-red-600 font-bold' : 'text-slate-400'
                }`}
              >
                {footer.length}/60
              </span>
            </div>
            <input
              type="text"
              maxLength={60}
              value={footer}
              onChange={(e) => setFooter(e.target.value)}
              placeholder={t('tpl.footerOptOutPlaceholder') || 'e.g. Reply STOP to unsubscribe'}
              className="w-full rounded-lg border border-slate-200 px-3 py-2 text-xs outline-none focus:border-indigo-500"
            />
            <p className="text-[10px] text-slate-400">
              Muted text at the bottom. No variables allowed in footers per Meta rules.
            </p>
          </div>
        )}

        {/* Section 5: Buttons Builder (General, Mixed, OTP) */}
        {!isCarousel && (
          <div className="space-y-3 rounded-xl border border-slate-200 bg-white p-4 shadow-sm">
            <div className="flex items-center justify-between">
              <h3 className="text-xs font-bold uppercase tracking-wider text-slate-700">
                {t('tpl.section.buttons') || '5. Buttons (Optional)'}
              </h3>
              <span className="text-xs text-slate-400">
                {buttons.length}/10 {t('tpl.buttons') || 'Buttons'}
              </span>
            </div>

            {/* Add Button Controls */}
            {buttons.length < 10 && templateType !== 'otp' && (
              <div className="flex flex-wrap gap-1.5">
                <button
                  type="button"
                  onClick={() => addButton('QUICK_REPLY')}
                  className="rounded-lg border border-slate-200 bg-slate-50 px-2.5 py-1 text-xs font-semibold text-slate-700 hover:bg-slate-100"
                >
                  + Quick Reply (max 3 in mixed)
                </button>
                <button
                  type="button"
                  onClick={() => addButton('URL')}
                  className="rounded-lg border border-slate-200 bg-slate-50 px-2.5 py-1 text-xs font-semibold text-slate-700 hover:bg-slate-100"
                >
                  + Visit Website (max 2)
                </button>
                <button
                  type="button"
                  onClick={() => addButton('PHONE_NUMBER')}
                  className="rounded-lg border border-slate-200 bg-slate-50 px-2.5 py-1 text-xs font-semibold text-slate-700 hover:bg-slate-100"
                >
                  + Call Phone (max 1)
                </button>
                {templateType === 'mixed' && (
                  <button
                    type="button"
                    onClick={() => addButton('COPY_CODE')}
                    className="rounded-lg border border-slate-200 bg-slate-50 px-2.5 py-1 text-xs font-semibold text-slate-700 hover:bg-slate-100"
                  >
                    + Copy Code (max 1)
                  </button>
                )}
              </div>
            )}

            {/* Button Rows */}
            <div className="space-y-2 pt-1">
              {buttons.map((b, idx) => (
                <div
                  key={idx}
                  className="flex flex-col sm:flex-row items-stretch sm:items-center gap-2 rounded-lg bg-slate-50 p-2.5 border border-slate-200"
                >
                  <span className="rounded bg-white px-2 py-1 text-[11px] font-bold text-slate-700 border border-slate-200 shrink-0">
                    {b.type}
                  </span>

                  <input
                    type="text"
                    value={b.text}
                    maxLength={25}
                    placeholder="Button Text (max 25)"
                    onChange={(e) => updateButton(idx, { text: e.target.value })}
                    className="flex-1 rounded-md border border-slate-200 bg-white px-2.5 py-1 text-xs outline-none focus:border-indigo-500"
                  />

                  {b.type === 'URL' && (
                    <input
                      type="text"
                      value={b.url || ''}
                      placeholder="https://example.com"
                      onChange={(e) => updateButton(idx, { url: e.target.value })}
                      className="flex-1 rounded-md border border-slate-200 bg-white px-2.5 py-1 text-xs font-mono outline-none focus:border-indigo-500"
                    />
                  )}

                  {b.type === 'PHONE_NUMBER' && (
                    <input
                      type="text"
                      value={b.phone_number || ''}
                      placeholder="+15551234567"
                      onChange={(e) => updateButton(idx, { phone_number: e.target.value })}
                      className="flex-1 rounded-md border border-slate-200 bg-white px-2.5 py-1 text-xs font-mono outline-none focus:border-indigo-500"
                    />
                  )}

                  <button
                    type="button"
                    onClick={() => removeButton(idx)}
                    className="h-7 w-7 shrink-0 rounded text-slate-400 hover:bg-red-50 hover:text-red-600 flex items-center justify-center"
                    title="Remove button"
                  >
                    ✕
                  </button>
                </div>
              ))}
            </div>

            {!mixedValidation.valid && (
              <div className="rounded-lg bg-amber-50 p-2 text-xs text-amber-700 font-medium">
                ⚠️ {mixedValidation.error}
              </div>
            )}
          </div>
        )}

        {/* Section 6: Carousel Cards Builder (Carousel Media / Product) */}
        {isCarousel && (
          <div className="rounded-xl border border-slate-200 bg-white p-4 shadow-sm">
            <CarouselCardsBuilder
              cards={carouselCards}
              onChange={setCarouselCards}
              isProductCarousel={templateType === 'carousel_product'}
              integrationId={integrationId || undefined}
            />
          </div>
        )}

        {/* Error message */}
        {submitError && (
          <div className="rounded-xl border border-red-200 bg-red-50 p-4 text-xs font-semibold text-red-700">
            ⚠️ {submitError}
          </div>
        )}
      </div>

      {/* AI Rewrite Modal */}
      <AiRewriteModal
        isOpen={aiModalOpen}
        onClose={() => setAiModalOpen(false)}
        currentBody={body}
        language={language}
        onApply={(newBody) => setBody(newBody)}
      />
    </Drawer>
  );
}
